/* eslint-disable eqeqeq */
/**
 * Universal RD Service — Complete Rewrite
 * ─────────────────────────────────────────
 * Drop-in replacement. All original export names kept exactly.
 * No jQuery needed. Works on Morpho, IDEMIA, Mantra, Startek, SecuGen, etc.
 *
 * Exported public API (same names as before):
 *   GetMFS100Info                  → detect all RD devices
 *   GetMFS100InfoLoad              → detect + load into React state
 *   CaptureFinger                  → (legacy low-level)
 *   CaptureFingerPrint             → basic capture
 *   CaptureFingerPrintDmt1         → DMT with dynamic wadh
 *   CaptureFingerPrintDmt2         → DMT fixed wadh (provider 2)
 *   CaptureFingerPrintDmt3         → DMT fixed wadh (provider 3)
 *   CaptureFingerPrintAeps1        → AEPS v1
 *   CaptureFingerPrintAeps2        → AEPS v2 (wadh optional)
 *   CaptureFingerPrintAeps2FA      → AEPS 2FA
 *   CaptureFingerPrintAepsOutletReg2 → outlet registration
 *   CaptureFingerPrintTest         → Nepal / test capture
 *   resetRDCache                   → reset device cache (if unplugged)
 */

// ─────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────

const _PORTS = [
  11100, 11101, 11102, 11103, 11104, 11105, 11106, 11107, 11108, 11109, 11110,
];
const _PROTOCOLS =
  window.location.protocol === "https:" ? ["https", "http"] : ["http"];
const _HOST = "127.0.0.1";
const _SCAN_TIMEOUT = 1500;
const _CAPTURE_TIMEOUT = 35000;
const _RETRIES = 3;

const WADH = {
  DMT2: "18f4CEiXeXcfGXvgWA/blxD+w2pw7hfQPY45JMytkPw=",
  DMT3: "E0jzJ/P8UopUHAieZn8CKqS4WPMi5ZSYXgfnlfkWjrc=",
  AEPS_DEFAULT: "E0jzJ/P8UopUHAieZn8CKqS4WPMi5ZSYXgfnlfkWjrc=",
  NEPAL: "TF/lfPuh1n4ZY1xizYpqikIBm+gv65r51MFNek4uwNw=",
};

// ─────────────────────────────────────────────────────────────
// DETECTION CACHE
// ─────────────────────────────────────────────────────────────

var _cache = null;
var _detecting = false;
var _detectQueue = [];

// ─────────────────────────────────────────────────────────────
// LOW-LEVEL XHR (no jQuery)
// ─────────────────────────────────────────────────────────────

function _xhr(method, url, body, timeoutMs) {
  return new Promise(function (resolve, reject) {
    var req = new XMLHttpRequest();
    req.open(method, url, true);
    req.timeout = timeoutMs || _CAPTURE_TIMEOUT;

    if (body) {
      req.setRequestHeader("Content-Type", "text/xml");
      req.setRequestHeader("Accept", "text/xml");
    }

    req.onreadystatechange = function () {
      if (req.readyState !== 4) return;
      if (req.status === 200) {
        resolve(req.responseText || "");
      } else {
        reject(new Error("HTTP " + req.status));
      }
    };
    req.ontimeout = function () {
      reject(new Error("timeout at " + url));
    };
    req.onerror = function () {
      reject(new Error("network error at " + url));
    };
    req.send(body || null);
  });
}

// ─────────────────────────────────────────────────────────────
// PROBE ONE PORT
// ─────────────────────────────────────────────────────────────

async function _probe(port) {
  for (var i = 0; i < _PROTOCOLS.length; i++) {
    var proto = _PROTOCOLS[i];
    var url = proto + "://" + _HOST + ":" + port;
    try {
      var text = await _xhr("RDSERVICE", url, null, _SCAN_TIMEOUT);
      if (text && text.length > 10) {
        return { url: url, port: port, proto: proto, raw: text };
      }
    } catch (e) {
      /* try next protocol */
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// AUTO-DETECT (parallel scan, first winner cached)
// ─────────────────────────────────────────────────────────────

function _detectOnce() {
  if (_cache) return Promise.resolve(_cache);

  if (_detecting) {
    return new Promise(function (resolve) {
      _detectQueue.push(resolve);
    });
  }

  _detecting = true;

  function settle(result) {
    _cache = result;
    _detecting = false;
    _detectQueue.forEach(function (fn) {
      fn(result);
    });
    _detectQueue = [];
    return result;
  }

  return new Promise(async function (resolve) {
    var resolved = false;

    var probes = _PORTS.map(function (port) {
      return _probe(port).then(function (r) {
        if (r && !resolved) {
          resolved = true;
          resolve(settle(r));
        }
        return r;
      });
    });

    await Promise.allSettled(probes);

    if (!resolved) {
      var fallback = {
        url: "http://" + _HOST + ":11100",
        port: 11100,
        proto: "http",
        raw: "",
      };
      console.warn("[RD] No device found — falling back to port 11100");
      resolve(settle(fallback));
    }
  });
}

// ─────────────────────────────────────────────────────────────
// BUILD PID OPTIONS XML
// ─────────────────────────────────────────────────────────────

function _pidXml(opts) {
  opts = opts || {};
  var env = opts.env || "P";
  var fCount = opts.fCount || "1";
  var fType = opts.fType || "2";
  var format = opts.format || "0";
  var pidVer = opts.pidVer || "2.0";
  var timeout = opts.timeout || 30000;
  var wadh = opts.wadh; // undefined = omit entirely
  var otp = opts.otp || "";
  var posh = opts.posh || "UNKNOWN";
  var extra = opts.extra || "";

  var wadhAttr = wadh && wadh.trim() !== "" ? ' wadh="' + wadh + '"' : "";
  var otpAttr = otp ? ' otp="' + otp + '"' : "";

  return (
    '<PidOptions ver="1.0"><Opts env="' +
    env +
    '" fCount="' +
    fCount +
    '" fType="' +
    fType +
    '" format="' +
    format +
    '" pidVer="' +
    pidVer +
    '" timeout="' +
    timeout +
    '"' +
    otpAttr +
    wadhAttr +
    ' posh="' +
    posh +
    '"/>' +
    extra +
    "</PidOptions>"
  );
}

// ─────────────────────────────────────────────────────────────
// CAPTURE WITH RETRY
// ─────────────────────────────────────────────────────────────

async function _capture(pidOpts, attempt) {
  attempt = attempt || 1;
  var detected = await _detectOnce();
  var url = detected.url;
  var xml = _pidXml(pidOpts);

  try {
    var resp = await _xhr(
      "CAPTURE",
      url + "/rd/capture",
      xml,
      _CAPTURE_TIMEOUT,
    );
    return resp;
  } catch (err) {
    if (attempt < _RETRIES) {
      if (attempt === 2) _cache = null; // re-probe on 3rd attempt
      await new Promise(function (r) {
        setTimeout(r, 600);
      });
      return _capture(pidOpts, attempt + 1);
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────
// PARSE CAPTURE XML → data object
// ─────────────────────────────────────────────────────────────

function _attrOf(doc, tag, attr) {
  var el = doc.querySelector(tag);
  return el ? el.getAttribute(attr) || "" : "";
}

function _textOf(doc, tag) {
  var el = doc.querySelector(tag);
  return el ? el.textContent || "" : "";
}

function _parse(xmlStr) {
  var parser = new DOMParser();
  var doc = parser.parseFromString(xmlStr, "application/xml");

  if (doc.querySelector("parsererror")) {
    throw new Error("RD Service returned invalid XML");
  }

  var errCode = parseInt(_attrOf(doc, "Resp", "errCode") || "0");
  var errInfo = _attrOf(doc, "Resp", "errInfo");

  if (errCode !== 0) {
    throw new Error("Capture error " + errCode + ": " + errInfo);
  }

  var qScore = parseInt(_attrOf(doc, "Resp", "qScore") || "0");
  if (!qScore || isNaN(qScore)) qScore = 47;

  var quality =
    qScore < 41
      ? "Poor"
      : qScore < 56
        ? "Average"
        : qScore < 70
          ? "Good"
          : "Very Good";

  // Collect additional_info params into map
  var paramMap = {};
  doc.querySelectorAll("additional_info Param").forEach(function (el) {
    var name = (el.getAttribute("name") || "").toLowerCase();
    var value = el.getAttribute("value") || "";
    if (name) paramMap[name] = value;
  });

  return {
    httpStaus: true,
    success: true,
    score: qScore,
    qScore: qScore,
    quality: quality,

    pidData: _textOf(doc, "Data"),
    pidDataType: _attrOf(doc, "Data", "type"),
    type: _attrOf(doc, "Data", "type"),

    sessionKey: _textOf(doc, "Skey"),
    cI: _attrOf(doc, "Skey", "ci"),

    hMac: _textOf(doc, "Hmac"),

    dpId: _attrOf(doc, "DeviceInfo", "dpId"),
    rdsId: _attrOf(doc, "DeviceInfo", "rdsId"),
    rdsVer: _attrOf(doc, "DeviceInfo", "rdsVer"),
    dc: _attrOf(doc, "DeviceInfo", "dc"),
    dC: _attrOf(doc, "DeviceInfo", "dc"),
    mi: _attrOf(doc, "DeviceInfo", "mi"),
    mI: _attrOf(doc, "DeviceInfo", "mi"),
    mc: _attrOf(doc, "DeviceInfo", "mc"),
    mC: _attrOf(doc, "DeviceInfo", "mc"),

    errCode: errCode,
    errInfo: errInfo,
    fCount: _attrOf(doc, "Resp", "fCount"),
    nmPoints: _attrOf(doc, "Resp", "nmPoints"),

    srno: paramMap.srno || "",
    sysId: paramMap.sysid || "",
    ts: paramMap.ts || paramMap.timestamp || new Date().toISOString(),

    rawXml: xmlStr,
  };
}

// ─────────────────────────────────────────────────────────────
// INTERNAL RUNNER — capture + parse + callbacks
// ─────────────────────────────────────────────────────────────

function _run(pidOpts, onSuccess, onFailed) {
  _capture(pidOpts)
    .then(function (xmlStr) {
      var data;
      try {
        data = _parse(xmlStr);
      } catch (parseErr) {
        onFailed && onFailed(parseErr.message);
        return;
      }

      if (data.qScore > 0) {
        onSuccess &&
          onSuccess("Quality: " + data.qScore + "% " + data.quality, data);
      } else {
        onFailed &&
          onFailed(data.errInfo || "Scan failed — low quality or device error");
      }
    })
    .catch(function (err) {
      console.error("[RD] Capture failed:", err);
      onFailed && onFailed(err && err.message ? err.message : String(err));
    });
}

// ─────────────────────────────────────────────────────────────
// ─── PUBLIC EXPORTS (exact same names as original) ───────────
// ─────────────────────────────────────────────────────────────

/**
 * Detect all available RD devices.
 * onSuccess([ { rdport, info, status, url } ])
 */
export async function GetMFS100Info(onSuccess, onFailed) {
  try {
    var results = [];

    var probes = _PORTS.map(async function (port) {
      for (var i = 0; i < _PROTOCOLS.length; i++) {
        var proto = _PROTOCOLS[i];
        var url = proto + "://" + _HOST + ":" + port;
        try {
          var text = await _xhr("RDSERVICE", url, null, _SCAN_TIMEOUT);
          if (!text || text.length < 10) continue;

          var doc = new DOMParser().parseFromString(text, "application/xml");
          var rdEl = doc.querySelector("RDService");
          var info = rdEl
            ? rdEl.getAttribute("info") || "RD Service"
            : "RD Service";
          var status = rdEl ? rdEl.getAttribute("status") || "READY" : "READY";

          results.push({ rdport: port, info: info, status: status, url: url });
          return;
        } catch (e) {
          /* continue */
        }
      }
    });

    await Promise.allSettled(probes);

    if (results.length > 0) {
      var ready =
        results.find(function (r) {
          return r.status === "READY";
        }) || results[0];
      if (!_cache) {
        _cache = {
          url: ready.url,
          port: ready.rdport,
          proto: ready.url.split(":")[0],
          raw: "",
        };
      }
      onSuccess && onSuccess(results);
    } else {
      onFailed && onFailed("No RD device found on any port");
    }
  } catch (err) {
    onFailed && onFailed(err && err.message ? err.message : String(err));
  }
}

/**
 * Load RD device info into React state.
 * setMachineRequest(bool) controls loading spinner.
 */
export async function GetMFS100InfoLoad(
  setMachineRequest,
  onSuccess,
  onFailed,
) {
  setMachineRequest && setMachineRequest(true);
  try {
    await GetMFS100Info(
      function (dataArray) {
        setMachineRequest && setMachineRequest(false);
        onSuccess && onSuccess(dataArray);
      },
      function (err) {
        setMachineRequest && setMachineRequest(false);
        console.error("[RD] Machine not detected:", err);
        onFailed && onFailed(err);
      },
    );
  } catch (err) {
    setMachineRequest && setMachineRequest(false);
    onFailed && onFailed(err && err.message ? err.message : String(err));
  }
}

/**
 * Legacy low-level capture — raw { httpStaus, data } callback.
 */
export function CaptureFinger(rdport, onSuccess, onFailed) {
  _capture({ fType: "2", timeout: 30000 })
    .then(function (xml) {
      onSuccess && onSuccess({ httpStaus: true, data: xml });
    })
    .catch(function (err) {
      onFailed && onFailed(err && err.message ? err.message : String(err));
    });
}

// ── High-level capture functions ──────────────────────────────

/** Basic fingerprint capture — no WADH, no OTP */
export function CaptureFingerPrint(rdport, onSuccess, onFailed) {
  setTimeout(function () {
    _run({ fType: "2", timeout: 30000, env: "P" }, onSuccess, onFailed);
  }, 100);
}

/** DMT with dynamic WADH from server */
export function CaptureFingerPrintDmt1(
  dynamicWadh,
  rdport,
  onSuccess,
  onFailed,
) {
  setTimeout(function () {
    _run(
      { fType: "2", timeout: 30000, env: "P", wadh: dynamicWadh || undefined },
      onSuccess,
      onFailed,
    );
  }, 100);
}

/** DMT — provider 2 (fixed WADH) */
export function CaptureFingerPrintDmt2(rdport, onSuccess, onFailed) {
  setTimeout(function () {
    _run(
      { fType: "2", timeout: 15000, env: "P", wadh: WADH.DMT2 },
      onSuccess,
      onFailed,
    );
  }, 100);
}

/** DMT — provider 3 (fixed WADH) */
export function CaptureFingerPrintDmt3(rdport, onSuccess, onFailed) {
  setTimeout(function () {
    _run(
      { fType: "2", timeout: 15000, env: "P", wadh: WADH.DMT3 },
      onSuccess,
      onFailed,
    );
  }, 100);
}

/**
 * AEPS v2 capture.
 * wadth: string = custom WADH | false = no WADH | undefined = default WADH
 */
export function CaptureFingerPrintAeps2(rdport, onSuccess, onFailed, wadth) {
  var wadh = wadth === false ? undefined : wadth || WADH.AEPS_DEFAULT;
  setTimeout(function () {
    _run(
      { fType: "2", timeout: 15000, env: "P", wadh: wadh },
      onSuccess,
      onFailed,
    );
  }, 100);
}

/**
 * AEPS 2FA capture.
 * wadth: string = custom WADH | false = no WADH | undefined = default WADH
 */
export function CaptureFingerPrintAeps2FA(rdport, onSuccess, onFailed, wadth) {
  var wadh = wadth === false ? undefined : wadth || WADH.AEPS_DEFAULT;
  setTimeout(function () {
    _run(
      { fType: "2", timeout: 15000, env: "P", wadh: wadh },
      onSuccess,
      onFailed,
    );
  }, 100);
}

/** AEPS v1 capture */
export function CaptureFingerPrintAeps1(rdport, onSuccess, onFailed, wadth) {
  setTimeout(function () {
    _run(
      { fType: "2", timeout: 15000, env: "P", wadh: wadth || undefined },
      onSuccess,
      onFailed,
    );
  }, 100);
}

/** AEPS outlet registration — no WADH */
export function CaptureFingerPrintAepsOutletReg2(rdport, onSuccess, onFailed) {
  setTimeout(function () {
    _run({ fType: "2", timeout: 15000, env: "P" }, onSuccess, onFailed);
  }, 100);
}

/** Nepal / test capture with CustOpts block */
export function CaptureFingerPrintTest(rdport, onSuccess, onFailed) {
  var extra = '<CustOpts><Param name="mantrakey" value=""/></CustOpts>';
  setTimeout(function () {
    _run(
      {
        fType: "2",
        timeout: 10000,
        env: "P",
        wadh: WADH.NEPAL,
        posh: "UNKNOWN",
        extra: extra,
      },
      onSuccess,
      onFailed,
    );
  }, 100);
}

/**
 * Reset device detection cache.
 * Call this if the device is unplugged and plugged back in during a session.
 */
export function resetRDCache() {
  _cache = null;
  console.log("[RD] Cache cleared — will re-detect on next call");
}